import './App.scss';
import React from 'react';
import {
    Bye,
    Invitation,
    Inviter,
    InviterOptions,
    Referral,
    Session,
    SessionDescriptionHandlerModifier,
    SessionState,
    UserAgent,
    UserAgentOptions,
    UserAgentState
} from 'sip.js';
import PreCall from "./pages/PreCall/PreCall";
import Call from "./pages/Call/Call";
import Calling from "./pages/IncomeCall/Calling";
import './i18n';
import i18next from "i18next";
import 'react-toastify/dist/ReactToastify.min.css';
import { toast, ToastContainer } from "react-toastify";
import { smartLocalStorage } from "./utils/smartLocalStorage";
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from "@emotion/react";
import ErrorPage from "./pages/ErrorPage/ErrorPage";
import { IncomingResponse } from "sip.js/lib/core";
import EasyCallMeComp from "./pages/EasyCallMe/EasyCallMeComp/EasyCallMeComp";
import { HelperUtil } from "./utils/helper";

export interface InterfaceCatched {
    error: Error,
    retry: boolean,
    retryTitle: string,
    retryFunc: Function,
}

export interface InterfaceEasyCallMeState {
    name: string;
    lastName: string;
    email: string;
    errorName: boolean;
    errorLastName: boolean;
    errorEmail: boolean;
    showAudioVideoSettings: boolean;
    domain: string;
    next_url: string;
    api_url: string;
    mode: string;
}

export interface InterfaceAppState {
    localMediaDevices: MediaDeviceInfo[];
    localMicrophones: MediaDeviceInfo[];
    localSpeakers: MediaDeviceInfo[];
    localCameras: MediaDeviceInfo[];
    selectedMicrophone: MediaDeviceInfo | null;
    selectedSpeaker: MediaDeviceInfo | null;
    selectedCamera: MediaDeviceInfo | null;
    audioOnly: boolean;
    rejectReason: string,
    terminateReason: string,
    remoteVideoTrack: boolean;
    localVideoTrack: boolean;
    speakerAvailable: boolean;
    withStyle: boolean;
    accessPageLogo: string;

    catched: InterfaceCatched | null,

    lastCallDurationSeconds: number;

    userAgent: UserAgent | null;
    userAgentState: UserAgentState;
    session: Session | Invitation | Inviter | null;
    sessionState: SessionState;
    configurations: InterfaceConfigurations | null;

    EasyCallMe: InterfaceEasyCallMeState | null;
    EasyCallMeBackup: InterfaceEasyCallMeState | null;

    autoStart: string | null;
    keepAutoStart: boolean;
}

export interface InterfaceConfigurations {
    websocket: string;
    token_uuid: string;
    guest_uuid: string;
    guest_name: string;
    guest_email: string;
    ice_servers: Array<InterfaceIceServer>,
    call_destination: string;
    domain: string;
    language: 'it' | 'en' | 'de' | 'fr' | 'es';
    back_url: string;
    h264_enable: boolean;
}

export interface InterfaceIceServer {
    urls: string;
    username?: string;
    credential?: string;
}


class App extends React.Component<any, InterfaceAppState> {
    circleBackDropInterval: any = null;

    // ----------------------------------------------------------------------

    reconnectionAttempts = 3;
    // Number of seconds to wait between reconnection attempts
    reconnectionDelay = 4;

    // Used to guard against overlapping reconnection attempts
    attemptingReconnection = false;
    // If false, reconnection attempts will be discontinued or otherwise prevented
    shouldBeConnected = true;

    timeout: any;

    audioOutgoing = new Audio();
    audioTestSpeaker = new Audio();

    localStorageKeyPrefix: string = 'easy-link-prefix-'

    stream: MediaStream | null = null;
    streamAudioVisualizer: MediaStream | null = null;

    // ----------------------------------------------------------------------

    mainColor: any;
    theme: any;

    constructor(props: any) {
        super(props);
        this.init = this.init.bind(this);
        this.afterViewMounted = this.afterViewMounted.bind(this);

        this.audioOutgoing.src = require('./assets/audio/outgoing.wav');
        this.audioOutgoing.loop = true;
        this.audioTestSpeaker.src = require('./assets/audio/speakertest.mp3');

        const debugLogs = smartLocalStorage.getItem(this.localStorageKeyPrefix + "debugLogs");
        (window as any)["easyLinkLogs"] = debugLogs === "true";
        (window as any)["showEasyLinkLogs"] = () => {
            (window as any)["easyLinkLogs"] = true;
            smartLocalStorage.setItem(this.localStorageKeyPrefix + "debugLogs", "true");
            return "Easy Link logs enabled = " + (window as any)["easyLinkLogs"];
        };
        (window as any)["hideEasyLinkLogs"] = () => {
            (window as any)["easyLinkLogs"] = false;
            smartLocalStorage.setItem(this.localStorageKeyPrefix + "debugLogs", "false");
            return "Easy Link logs enabled = " + (window as any)["easyLinkLogs"];
        };

        this.init(true);
    }

    init(fromConstructor: boolean, callBack?: Function) {
        const Color = require('color');

        this.mainColor = Color(((window as any)._easy_link_config && (window as any)._easy_link_config.main_color) ?
            (window as any)._easy_link_config.main_color : '#067ebe');

        this.theme = createTheme({
            palette: {
                primary: {
                    light: this.mainColor.lighten(0.3).hex(),
                    main: this.mainColor.hex(),
                    dark: this.mainColor.darken(0.1).hex(),
                },
                secondary: {
                    light: '#838383',
                    main: '#3d3d3d',
                    dark: '#1a1a1a',
                },
                success: {
                    light: '#b7e55c',
                    main: '#659406',
                    dark: '#4c6e08',
                },
                error: {
                    light: '#da6b48',
                    main: '#b73108',
                    dark: '#83270b',
                },
            },
        });

        let EasyCallMe: InterfaceEasyCallMeState | null = null;


        if ((window as any)._easy_link_config.step === 'request_data') {
            EasyCallMe = {
                name: '',
                lastName: '',
                email: '',
                errorName: false,
                errorLastName: false,
                errorEmail: false,
                showAudioVideoSettings: false,
                domain: (window as any)._easy_link_config.domain,
                next_url: (window as any)._easy_link_config.next_url,
                api_url: (window as any)._easy_link_config.api_url,
                mode: (window as any)._easy_link_config.mode
            };
        }

        let search = window.location.search;
        let params = new URLSearchParams(search);
        let autoStart = params.get('autostart');

        const state: InterfaceAppState = {
            localMediaDevices: [],
            localMicrophones: [],
            localSpeakers: [],
            localCameras: [],
            selectedMicrophone: null,
            selectedSpeaker: null,
            selectedCamera: null,
            audioOnly: true,
            rejectReason: '',
            terminateReason: '',
            remoteVideoTrack: false,
            localVideoTrack: false,
            speakerAvailable: false,
            withStyle: this.state ? this.state.withStyle : false,
            accessPageLogo: this.state?.accessPageLogo ?? '',

            catched: null,

            lastCallDurationSeconds: 0,

            userAgent: null,
            userAgentState: UserAgentState.Stopped,
            session: null,
            sessionState: SessionState.Initial,
            configurations: null,

            EasyCallMe: EasyCallMe,

            EasyCallMeBackup: (window as any)._easy_link_config.step === 'request_data' ? EasyCallMe :
                (window as any)._easy_link_config.step === 'call' ? EasyCallMe ?
                    EasyCallMe : this.state?.EasyCallMeBackup : null,

            autoStart: autoStart,
            keepAutoStart: this.state?.keepAutoStart ? this.state?.keepAutoStart :
                (window as any)._easy_link_config.step === 'request_data' ? true : false
        };

        if (fromConstructor) {
            this.state = state;
        } else {
            this.setState(state, () => {
                if (callBack) {
                    callBack();
                }
            });
        }
    }

    playAudioOutgoing = () => {
        this.audioOutgoing.currentTime = 0;
        this.audioOutgoing.play();
    }

    stopAudioOutgoing = () => {
        this.audioOutgoing.pause();
    }

    playAudioTestSpeaker = () => {
        this.audioTestSpeaker.currentTime = 0;
        this.audioTestSpeaker.play();
    }

    stopAudioTestSpeaker = () => {
        this.audioTestSpeaker.pause();
    }

    testSpeaker(device: MediaDeviceInfo) {
        this.stopAudioTestSpeaker();
        if (device) {
            (this.audioTestSpeaker as any).setSinkId(device.deviceId).then(() => {
                this.playAudioTestSpeaker();
            }).catch((e: Error) => {
                if (HelperUtil.showLogs()) {
                    console.log('Couldn\'t set Sink Id to the html media element. Error:', e);
                }
            });
        }
    }

    setupSpeakerVideo(device: MediaDeviceInfo | null, videoElementOrId: string | HTMLVideoElement | any, doneCallback?: Function, failedCallback?: Function) {
        const videoElement: HTMLVideoElement | HTMLElement | null = typeof videoElementOrId === 'string' ?
            document.getElementById(videoElementOrId) : videoElementOrId;

        if (videoElement) {
            (videoElement as HTMLVideoElement).srcObject = null;
            (videoElement as HTMLVideoElement).pause();

            if (device && typeof (videoElement as any).sinkId !== 'undefined') {
                (videoElement as any).setSinkId(device.deviceId).then(() => {
                    if (doneCallback) {
                        doneCallback();
                    }
                }).catch((e: Error) => {
                    if (doneCallback) {
                        doneCallback();
                    }
                });
            } else {
                if (doneCallback) {
                    doneCallback();
                }
            }
        } else {
            if (failedCallback) {
                failedCallback();
            }
        }
    }

    render() {
        const EasyCallMe = this.state.EasyCallMe;
        console.log(EasyCallMe)
        return (
            <ThemeProvider theme={this.theme}>
                <div className="App">
                    {EasyCallMe ? <>
                        <EasyCallMeComp AppComp={this} AppState={this.state} />
                    </> :
                        <>
                            {!this.state.session && <PreCall AppComp={this} AppState={this.state} />}
                            {this.state.session && <>
                                {this.state.session.state === SessionState.Established ?
                                    <Call AppComp={this} AppState={this.state} />
                                    :
                                    <Calling AppComp={this} AppState={this.state} />}
                            </>}
                        </>
                    }
                    {
                        this.state.catched && <ErrorPage AppComp={this} AppState={this.state} />
                    }
                    <ToastContainer />
                </div>
            </ThemeProvider>
        );
    }

    componentDidMount() {
        if (this.state.EasyCallMe && this.state.EasyCallMe.mode && this.state.EasyCallMe.mode !== 'embedded' && !this.state.withStyle) {
            this.setState({
                withStyle: true,
            });

            const img = new Image();

            const logoUrl = 'https://' + (this.state.EasyCallMe && this.state.EasyCallMe.domain ? this.state.EasyCallMe.domain : window.location.origin) + '/branding/logo-login.png';

            img.onload = () => {
                this.setState({
                    accessPageLogo: logoUrl
                });
            };

            img.src = logoUrl;
        }
        this.afterViewMounted();
    }

    afterViewMounted() {
        let speakerAvailable: boolean;
        const audio: any = new Audio();

        if (typeof audio.sinkId === 'undefined') {
            speakerAvailable = false;
        } else {
            speakerAvailable = true;
        }

        this.setState({
            speakerAvailable
        }, () => {
            if (!this.state.EasyCallMe) {
                this.initializeUserMedia();
                this.initConfigurations();
            }
        });
    }

    initializeUserMedia = () => {
        this.enumerateDevices(devices => {
            const filteredDevices = this.filterDevices(devices);

            const constraints: MediaStreamConstraints = {
                audio: !!this.filterMicrophones(filteredDevices).length,
                video: !!this.filterCameras(filteredDevices).length
            };

            if (!constraints.audio) {
                if (HelperUtil.showLogs()) {
                    console.log('No audio input device found');
                }
                toast.error(i18next.t('no_input_device'), {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false,
                    progress: undefined,
                });
                return;
            }

            this.stopStream(null);
            navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
                if (stream) {
                    this.stopStream(stream);
                }

                this.initMediaDevices();
            }).catch((error: Error) => {
                if (error) {
                    if (HelperUtil.showLogs()) {
                        console.log('There was an error obtaining the stream', error.message, error.cause, error.name);
                    }
                }

                if (error && (error.message === 'Permission denied' ||
                    error.message === 'The request is not allowed by the user agent or the platform in the current context.' ||
                    error.message === 'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.') &&
                    error.name === 'NotAllowedError') {
                    this.setState({
                        catched: {
                            error: new Error(i18next.t("no_device_permissions")),
                            retry: true,
                            retryTitle: i18next.t("reload"),
                            retryFunc: () => {
                                this.setState({
                                    catched: null
                                }, () => {
                                    window.location.reload();
                                });
                            }
                        }
                    });
                } else {
                    this.initMediaDevices();
                }
            });
        }, error => {
            if (HelperUtil.showLogs()) {
                console.log('There was an error obtaining devices. Error:', error);
            }
            this.setState({
                catched: {
                    error: new Error(i18next.t("initializa_user_media_error")),
                    retry: true,
                    retryTitle: '',
                    retryFunc: () => {
                        this.setState({
                            catched: null
                        }, () => {
                            this.initializeUserMedia();
                        });
                    }
                }
            });
        });
    };

    stopStream = (streamParam: MediaStream | null) => {
        if (streamParam) {
            const tracks = streamParam.getTracks();

            tracks.forEach((track) => {
                if (track) {
                    track.stop();
                }
            });

            streamParam = null;
        } else {
            if (this.stream) {
                const tracks = this.stream.getTracks();

                tracks.forEach((track) => {
                    if (track) {
                        track.stop();
                    }
                });

                this.stream = null;
            }
        }
    };

    // ----------------------------------------------------------------------

    initConfigurations = () => {
        if ((window as any)._easy_link_config) {
            const configurations = (window as any)._easy_link_config;
            if (configurations.websocket && configurations.token_uuid && configurations.guest_uuid && configurations.call_destination && configurations.domain) {
                const iceServers: Array<InterfaceIceServer> = [];

                if (configurations.stun_servers && configurations.stun_servers.length) {
                    (configurations.stun_servers as Array<any>).forEach((i) => {
                        iceServers.push({
                            urls: i['urls'],
                        });
                    });
                }

                if (configurations.turn_servers && configurations.turn_servers.length) {
                    (configurations.turn_servers as Array<any>).forEach((i) => {
                        iceServers.push({
                            urls: i['urls'],
                            username: i['username'],
                            credential: i['credential'],
                        });
                    });
                }

                this.setState({
                    configurations: {
                        websocket: configurations.websocket,
                        token_uuid: configurations.token_uuid,
                        guest_uuid: configurations.guest_uuid,
                        guest_name: configurations.guest_name,
                        guest_email: configurations.guest_email,
                        ice_servers: iceServers,
                        call_destination: configurations.call_destination,
                        domain: configurations.domain,
                        language: configurations.language ? configurations.language : 'it',
                        back_url: configurations.back_url,
                        h264_enable: configurations.h264_enable
                    }
                }, () => {
                    i18next.changeLanguage(this.state.configurations?.language);
                    this.initUserAgent();
                });
            } else {
                this.setState({
                    catched: {
                        error: new Error(i18next.t("missing_configurations")),
                        retry: false,
                        retryTitle: '',
                        retryFunc: () => {
                            this.setState({
                                catched: null
                            }, () => {
                                window.location.reload();
                            });
                        }
                    }
                });
            }
        } else {
            this.setState({
                catched: {
                    error: new Error(i18next.t("no_configurations")),
                    retry: false,
                    retryTitle: '',
                    retryFunc: () => {
                        this.setState({
                            catched: null
                        }, () => {
                            window.location.reload();
                        });
                    }
                }
            });
        }
    };

    initMediaDevices = () => {
        window.navigator.mediaDevices.ondevicechange = () => {
            if (HelperUtil.showLogs()) {
                console.log('One of the media devices has been changed');
            }
            this.enumerateDevices();
        };

        this.enumerateDevices();
    };

    enumerateDevices = (thenCallback?: (devices: MediaDeviceInfo[]) => void, catchCallback?: (error: Error) => void) => {
        navigator.mediaDevices.enumerateDevices()
            .then((devices) => {
                if (!thenCallback) {
                    if (HelperUtil.showLogs()) {
                        console.log('List of devices after we obtained them:', devices);
                    }
                    const filteredDevices = this.filterDevices(devices);
                    if (HelperUtil.showLogs()) {
                        console.log('List of devices after we filtered them:', filteredDevices);
                    }
                    this.setState({
                        localMediaDevices: filteredDevices,
                        localMicrophones: this.filterMicrophones(filteredDevices),
                        localSpeakers: this.filterSpeakers(filteredDevices),
                        localCameras: this.filterCameras(filteredDevices),
                    }, () => {
                        this.controllSelectedDevices();
                    });
                } else {
                    thenCallback(devices);
                }
            })
            .catch(error => {
                if (!catchCallback) {
                    if (HelperUtil.showLogs()) {
                        console.log('There was an error obtaining the devices. Error:', error);
                    }
                    this.setState({
                        catched: {
                            error: new Error(i18next.t("couldnt_enumerate_devices")),
                            retry: true,
                            retryTitle: '',
                            retryFunc: () => {
                                this.setState({
                                    catched: null
                                }, () => {
                                    this.enumerateDevices(thenCallback, catchCallback);
                                });
                            }
                        }
                    });
                } else {
                    catchCallback(error);
                }
            });
    };

    filterDevices = (devices: MediaDeviceInfo[]) => {
        return devices.filter((d: MediaDeviceInfo) => d.kind === 'videoinput' || d.kind === 'audioinput' || d.kind === 'audiooutput');
    };

    filterMicrophones = (devices: MediaDeviceInfo[]) => {
        return devices.filter((d: MediaDeviceInfo) => d.kind === 'audioinput');
    };

    filterSpeakers = (devices: MediaDeviceInfo[]) => {
        return devices.filter((d: MediaDeviceInfo) => d.kind === 'audiooutput');
    };

    filterCameras = (devices: MediaDeviceInfo[]) => {
        return devices.filter((d: MediaDeviceInfo) => d.kind === 'videoinput');
    };

    controllSelectedDevices = () => {
        let selectedCam: MediaDeviceInfo | null;
        let gotselectedMic: boolean = false;
        let gotselectedSpk: boolean = false;
        let gotselectedCam: boolean = false;

        const conti = () => {
            if (gotselectedMic && gotselectedCam && gotselectedSpk) {
                if (selectedCam) {
                    const constraints: MediaStreamConstraints = {
                        audio: false,
                        video: { deviceId: selectedCam.deviceId }
                    };

                    this.initPreviewVideo(constraints);
                }
            }
        };

        this.getSelectMicrophone(selectedMicrophone => {
            gotselectedMic = true;
            conti();
        });

        this.getSelectSpeaker(selectedSpeaker => {
            gotselectedSpk = true;
            conti();
        });

        this.getSelectCamera(selectedCamera => {
            selectedCam = selectedCamera;
            gotselectedCam = true;
            conti();
        });

        this.getAudioOnly((value) => {

        });
    };

    selectMicrophone = (device: MediaDeviceInfo | null, callback: (selectedMicrophone: MediaDeviceInfo | null) => void) => {
        const localMics = this.state.localMicrophones;
        if (!localMics.length) {
            this.setState({
                selectedMicrophone: null
            }, () => {
                smartLocalStorage.removeItem(this.localStorageKeyPrefix + 'selectedMicrophone');
                callback(this.state.selectedMicrophone);
            });

            return;
        }

        // @ts-ignore
        if (!device || !localMics.find(d => d.deviceId === device.deviceId)) {
            device = localMics[0];
        }

        this.setState({
            selectedMicrophone: device
        }, () => {
            smartLocalStorage.setItem(this.localStorageKeyPrefix + 'selectedMicrophone', JSON.stringify(device));

            this.visualizeAudio();

            callback(this.state.selectedMicrophone);
        });
    };

    selectSpeaker = (device: MediaDeviceInfo | null, callback: (selectedSpeaker: MediaDeviceInfo | null) => void) => {
        const localSpks = this.state.localSpeakers;
        if (!localSpks.length) {
            this.setState({
                selectedSpeaker: null
            }, () => {
                smartLocalStorage.removeItem(this.localStorageKeyPrefix + 'selectedSpeaker');
                callback(this.state.selectedSpeaker);
            });

            return;
        }

        // @ts-ignore
        if (!device || !localSpks.find(d => d.deviceId === device.deviceId)) {
            device = localSpks[0];
        }

        this.setState({
            selectedSpeaker: device
        }, () => {
            smartLocalStorage.setItem(this.localStorageKeyPrefix + 'selectedSpeaker', JSON.stringify(device));
            callback(this.state.selectedSpeaker);
        });
    };

    selectCamera = (device: MediaDeviceInfo | null, callback: (selectedCamera: MediaDeviceInfo | null) => void) => {
        const localCams = this.state.localCameras;
        if (!localCams.length) {
            this.setState({
                selectedCamera: null
            }, () => {
                smartLocalStorage.removeItem(this.localStorageKeyPrefix + 'selectedCamera');
                callback(null);
            });

            return;
        }

        // @ts-ignore
        if (!device || !localCams.find(d => d.deviceId === device.deviceId)) {
            device = localCams[0];
        }

        this.setState({
            selectedCamera: device
        }, () => {
            smartLocalStorage.setItem(this.localStorageKeyPrefix + 'selectedCamera', JSON.stringify(device));
            callback(this.state.selectedCamera);
        });
    };

    setAudioOnly = (value: boolean, callback: (value: boolean) => void) => {
        smartLocalStorage.setItem(this.localStorageKeyPrefix + 'audioOnly', value ? 'true' : 'false');
        this.setState({
            audioOnly: value
        }, () => {
            callback(value);
        });
    };

    getSelectMicrophone = (callback: (selectedMicrophone: MediaDeviceInfo | null) => void): void => {
        const selectedMic = smartLocalStorage.getItem(this.localStorageKeyPrefix + 'selectedMicrophone');
        const localMics = this.state.localMicrophones;

        if (!selectedMic || !localMics.find(d => d.deviceId === JSON.parse(selectedMic).deviceId)) {
            this.selectMicrophone(null, callback);
            return;
        }

        this.selectMicrophone(JSON.parse(selectedMic as string), callback);
    };

    getSelectSpeaker = (callback: (selectedSpeaker: MediaDeviceInfo | null) => void): void => {
        const selectedSpk = smartLocalStorage.getItem(this.localStorageKeyPrefix + 'selectedSpeaker');
        const localSpks = this.state.localSpeakers;

        if (!selectedSpk || !localSpks.find(d => d.deviceId === JSON.parse(selectedSpk).deviceId)) {
            this.selectSpeaker(null, callback);
            return;
        }

        this.selectSpeaker(JSON.parse(selectedSpk as string), callback);
    };

    getSelectCamera = (callback: (selectedCamera: MediaDeviceInfo | null) => void): void => {
        const selectedCam = smartLocalStorage.getItem(this.localStorageKeyPrefix + 'selectedCamera');
        const localCams = this.state.localCameras;

        if (!selectedCam || !localCams.find(d => d.deviceId === JSON.parse(selectedCam).deviceId)) {
            this.selectCamera(null, callback);
            return;
        }

        this.selectCamera(JSON.parse(selectedCam as string), callback);
    };

    getAudioOnly = (callback: (value: boolean) => void): boolean => {
        const audioOnly = smartLocalStorage.getItem(this.localStorageKeyPrefix + 'audioOnly');
        const value = !!(audioOnly && audioOnly === 'true');

        this.setAudioOnly(value, callback);

        return value;
    };

    // ----------------------------------------------------------------------

    visualizeAudio = () => {
        if (this.state.selectedMicrophone) {
            const constraints: MediaStreamConstraints = {
                audio: { deviceId: this.state.selectedMicrophone.deviceId },
                video: false
            };

            this.stopStream(this.streamAudioVisualizer);
            navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
                if (HelperUtil.showLogs()) {
                    console.log('The stream was successfully obtained while initializing the audio visualizer', stream);
                }

                if (stream) {
                    this.streamAudioVisualizer = stream;
                    const audio: HTMLElement | null = document.getElementById('audio-audio-visualizer');

                    this.setupStreamToVideo(stream, audio, () => {
                        /*
                                                const context = new AudioContext();
                                                //audio from the file
                                                const src = context.createMediaElementSource(audio as HTMLMediaElement);
                                                const analyser = context.createAnalyser();

                                                // @ts-ignore
                                                const canvas: HTMLCanvasElement | null = document.getElementById('canvas-audio-visualizer');
                                                const ctx = (canvas as HTMLCanvasElement).getContext("2d");
                                                src.connect(analyser);
                                                analyser.connect(context.destination);
                                                analyser.fftSize = 256;

                                                const bufferLength = analyser.frequencyBinCount;
                                                const dataArray = new Uint8Array(bufferLength);

                                                const WIDTH = (canvas as HTMLCanvasElement).width;
                                                const HEIGHT = (canvas as HTMLCanvasElement).height;
                                                const barWidth = (WIDTH / bufferLength) * 1.5;
                                                let barHeight;
                                                let x = 0;

                                                function renderFrame() {
                                                    requestAnimationFrame(renderFrame);

                                                    x = 0;

                                                    analyser.getByteFrequencyData(dataArray);

                                                    // @ts-ignore
                                                    ctx.clearRect(0, 0, WIDTH, HEIGHT);

                                                    for (var i = 0; i < bufferLength; i++) {
                                                        barHeight = dataArray[i];

                                                        // @ts-ignore
                                                        var gradient = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
                                                        gradient.addColorStop(0, '#ffb7c5');
                                                        gradient.addColorStop(0.5, '#dc143c');
                                                        gradient.addColorStop(1.0, '#000');
                                                        // @ts-ignore
                                                        ctx.fillStyle = gradient;
                                                        // @ts-ignore
                                                        ctx.font = "100px Georgia";
                                                        // @ts-ignore
                                                        ctx.fillText("|", x, HEIGHT - barHeight);

                                                        x += barWidth + 2;
                                                    }
                                                }

                                                (audio as HTMLAudioElement).play();
                                                renderFrame();
                        */
                    });
                }
            }).catch((err) => {
                if (err) {
                    if (HelperUtil.showLogs()) {
                        console.error('There was an error obtaining the stream while initializing the audio visualizer', err);
                    }
                }
            });
        }
    }

    // ----------------------------------------------------------------------

    initPreviewVideo = (constraints?: MediaStreamConstraints) => {
        if (!constraints) {
            const selectedCam = this.state.selectedCamera;
            if (selectedCam) {
                constraints = {
                    audio: false,
                    video: { deviceId: selectedCam.deviceId }
                };
            }
        }

        if (constraints) {
            this.stopStream(null);
            navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
                if (HelperUtil.showLogs()) {
                    console.log('The stream was successfully obtained while initializing the preview', stream);
                }

                if (stream) {
                    this.stream = stream;
                    this.setupStreamToVideo(stream, 'pre-call-video');
                }
            }).catch((err) => {
                if (err) {
                    if (HelperUtil.showLogs()) {
                        console.error('There was an error obtaining the stream while initializing the preview', err);
                    }
                }
            });
        }
    };

    /*
        initCircles = () => {
            if (this.circleBackDropInterval) {
                clearInterval(this.circleBackDropInterval);
            }

            this.circleBackDropInterval = setInterval(() => {
                this.setState({
                    circleBackDropFilter: `hue-rotate(${Math.random() * 500}deg) saturate(${Math.random() * 500}%) contrast(${Math.random() * 500}%)`
                })
            }, 10000);
        };
    */

    // ----------------------------------------------------------------------

    setupStreamToVideo(stream: MediaStream | null, videoElementOrId: string | any, doneCallback?: Function, failedCallback?: Function) {
        const videoElement: HTMLVideoElement | HTMLElement | null = typeof videoElementOrId === 'string' ?
            document.getElementById(videoElementOrId) : videoElementOrId;

        if (videoElement) {
            (videoElement as HTMLVideoElement).srcObject = null;
            (videoElement as HTMLVideoElement).pause();

            if (stream) {
                (videoElement as HTMLVideoElement).srcObject = stream;

                (videoElement as HTMLVideoElement).play();
            }

            if (doneCallback) {
                doneCallback();
            }
        } else {
            if (HelperUtil.showLogs()) {
                console.warn('Video element not present or not in the correct format');
            }
            if (failedCallback) {
                failedCallback();
            }
        }
    }

    // ----------------------------------------------------------------------

    initUserAgent = (): void => {
        const sipUsername = `sip:` + this.state.configurations?.guest_email;


        const transportOptions = {
            server: this.state.configurations?.websocket
        };

        const uri = UserAgent.makeURI(sipUsername);

        const userAgentOptions: UserAgentOptions = {
            authorizationPassword: "",
            authorizationUsername: "",
            transportOptions,
            uri,
            logBuiltinEnabled: HelperUtil.showLogs(),
            hackViaWs: true,
            contactParams: { transport: 'ws' },
            sessionDescriptionHandlerFactoryOptions: {
                iceGatheringTimeout: 500,
                peerConnectionConfiguration: {
                    iceServers: this.state.configurations?.ice_servers
                },
                offerOptions: {
                    iceRestart: true
                }
            }
        };

        const userAgent: UserAgent = new UserAgent(userAgentOptions);

        userAgent.delegate = {
            // onInvite: this.onInvite
        };

        userAgent.transport.stateChange.addListener((data) => {
            if (HelperUtil.showLogs()) {
                console.log('New transport state', data);
            }
            if (data === "Disconnected") {
                toast.error(i18next.t('transport_error'), {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false,
                    progress: undefined,
                });
            }
        });

        // Handle connection with server established
        userAgent.delegate.onConnect = () => {
            // On connecting, register the user agent
            if (HelperUtil.showLogs()) {
                console.log('User agent correctly connected');
            }
            this.setState({ userAgentState: userAgent.state }, () => {
                toast.success(i18next.t('connection_succesfull'), {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false,
                    progress: undefined,
                });
            });
        };

        // Handle connection with server lost
        userAgent.delegate.onDisconnect = (error?: Error) => {
            // On disconnect, cleanup invalid registrations
            if (HelperUtil.showLogs()) {
                console.log('User agent disconnected', error);
            }
            this.setState({ userAgentState: userAgent.state }, () => {

            });

            // Only attempt to reconnect if network/server dropped the connection (if there is an error)
            if (error) {
                this.attemptReconnection();
            }

            this.stopAudioOutgoing();
        };

        // Monitor network connectivity and attempt reconnection when browser goes online
        // @ts-ignore
        window.addEventListener("online", this.attemptReconnection);

        this.setState({
            userAgent: userAgent
        }, () => {
            /*
             * Start the user agent
             */
            if (HelperUtil.showLogs()) {
                console.log('User agent will be started', userAgent);
            }
            userAgent.start().then(() => {
                if (HelperUtil.showLogs()) {
                    console.log('User agent correctly started');
                }
                this.setState({ userAgentState: userAgent.state }, () => {
                });
            });
        });
    }

    cleanupUserAgent = (): void => {
        // @ts-ignore
        window.removeEventListener("online", this.attemptReconnection);

        this.state.userAgent?.stop().then(() => {
            if (HelperUtil.showLogs()) {
                console.log('User agent was stopped');
            }
            this.setState({
                userAgentState: this.state.userAgent?.state as UserAgentState,
                session: null,
                sessionState: SessionState.Initial
            }, () => {
            });
        });
    }

    call = (user: string | undefined, constrainsDefault: MediaStreamConstraints): void => {
        this.setState({
            lastCallDurationSeconds: 0
        }, () => {
            if (user === undefined) {
                if (HelperUtil.showLogs()) {
                    console.log('No user defined to call');
                }
                return;
            }
            if (HelperUtil.showLogs()) {
                console.log('A new call will be started to', user, constrainsDefault);
            }
            if (this.state.userAgent && this.state.userAgentState === UserAgentState.Started) {
                // Send an outgoing INVITE request
                const target = UserAgent.makeURI(`sip:${this.generateFullUser(user)}`);
                if (!target) {
                    throw new Error("Failed to create target URI.");
                }
                let video = true;
                if (constrainsDefault && constrainsDefault.video === false) {
                    video = false;
                }

                // Create a new Inviter
                const inviterOptions: InviterOptions = {
                    sessionDescriptionHandlerOptions: {
                        constraints: constrainsDefault,
                    },
                    extraHeaders: [
                        'X-Callme-Token-UUID: ' + this.state.configurations?.token_uuid,
                        'X-Callme-Guest-UUID: ' + this.state.configurations?.guest_uuid,
                        'X-NexiUC-video-mode: ' + video,
                        'X-NexiUC-suppress-cng: true',
                    ],
                };

                // check h264
                if (this.state.configurations?.h264_enable === false) {
                    const stripPayload = (sdp: string, payload: string): string => {
                        const mediaDescs: Array<any> = [];
                        let currentMediaDesc: any;
                        let lines: Array<string> = sdp.split(/\r\n/);

                        const lineNumbers: Array<number> = [];
                        const payloadNumbers: Array<string> = [];
                        const payloadRelativeNumbers: Array<string> = [];

                        for (let i = 0; i < lines.length;) {
                            const line: string = lines[i];
                            if (line.includes(payload)) {
                                const n = (line.replace('a=rtpmap:', '') as string).split(' ')[0];
                                if (n) {
                                    payloadNumbers.push(n);
                                }
                            }
                            i++;
                        }
                        payloadNumbers.forEach((n) => {
                            for (let i = 0; i < lines.length;) {
                                const line: string = lines[i];
                                if (line.includes('apt=' + n)) {
                                    const rn = (line.replace('a=fmtp:', '') as string).split(' ')[0];
                                    if (rn) {
                                        payloadRelativeNumbers.push(rn);
                                    }
                                }
                                i++;
                            }
                        });

                        payloadNumbers.forEach((n) => {
                            for (let i = 0; i < lines.length;) {
                                const line: string = lines[i];
                                if (line.includes('a=rtcp-fb:' + n)) {
                                    lineNumbers.push(i);
                                }
                                if (line.includes('a=fmtp:' + n)) {
                                    lineNumbers.push(i);
                                }
                                i++;
                            }
                        });

                        payloadRelativeNumbers.forEach((n) => {
                            for (let i = 0; i < lines.length;) {
                                const line: string = lines[i];
                                if (line.includes('a=rtpmap:' + n)) {
                                    lineNumbers.push(i);
                                }
                                if (line.includes('a=fmtp:' + n)) {
                                    lineNumbers.push(i);
                                }
                                i++;
                            }
                        });

                        const newlines = lines.filter((x, i) => {
                            if (!lineNumbers.includes(i)) {
                                return true;
                            } else {
                                return false;
                            }
                        });

                        lines = newlines;

                        for (let i = 0; i < lines.length;) {
                            const line: string = lines[i];
                            if (/^m=(?:audio|video)/.test(line)) {
                                currentMediaDesc = {
                                    index: i,
                                    stripped: []
                                };
                                mediaDescs.push(currentMediaDesc);
                            } else if (currentMediaDesc) {
                                const rtpmap = /^a=rtpmap:(\d+) ([^/]+)\//.exec(line);
                                if (rtpmap && payload === rtpmap[2]) {
                                    lines.splice(i, 1);
                                    currentMediaDesc.stripped.push(rtpmap[1]);
                                    continue; // Don't increment 'i'
                                }
                            }
                            i++;
                        }

                        if (currentMediaDesc !== null && currentMediaDesc !== undefined) {
                            payloadRelativeNumbers.forEach((n) => {
                                currentMediaDesc.stripped.push(n);
                            });
                        }

                        for (const mediaDesc of mediaDescs) {
                            const mline: Array<string> = lines[mediaDesc.index].split(' ');

                            // Ignore the first 3 parameters of the mline. The codec information is after that
                            for (let j = 3; j < mline.length;) {
                                if (mediaDesc.stripped.indexOf(mline[j]) !== -1) {
                                    mline.splice(j, 1);
                                    continue;
                                }
                                j++;
                            }

                            lines[mediaDesc.index] = mline.join(' ');
                        }

                        return lines.join('\r\n');
                    };

                    const modifier: SessionDescriptionHandlerModifier = (description) => {
                        let { sdp = '' } = description

                        // modify the sdp
                        description.sdp = stripPayload(sdp || '', 'H264');

                        return Promise.resolve(description)
                    }

                    inviterOptions.sessionDescriptionHandlerModifiers = [modifier];
                }

                const inviter = new Inviter(this.state.userAgent, target, inviterOptions);

                // An Inviter is a Session
                const session: Session = inviter;

                // Setup outgoing session delegate
                session.delegate = {
                    // Handle incoming REFER request.
                    onRefer: (referral: Referral): void => {
                        // ...
                    },
                    onBye: (bye: Bye) => {
                        /*
                                                const request = bye.request;
                                                const reason = request.headers.Reason;
                                                if (reason && reason.length) {
                                                    const r = reason[0].raw;
                                                    if (r) {
                                                        const regex = /text="(.+?)"$/g;
                                                        const result = regex.exec(r);
                                                        if (result && result[1]) {
                                                            const reasonCode = result[1];

                                                            if (reasonCode === "NORMAL_CLEARING") {
                                                                this.setState({terminateReason: i18next.t("NORMAL_CLEARING") as any});
                                                            } else if (reasonCode === "NORMAL_CLEARING") {
                                                                this.setState({terminateReason: i18next.t("NORMAL_CLEARING") as any});
                                                            } else {
                                                                this.setState({terminateReason: i18next.t("NORMAL_CLEARING") as any});
                                                            }
                                                        }
                                                    }
                                                }
                        */
                    },
                };

                // Handle outgoing session state changes.
                session.stateChange.addListener((state: SessionState) => {
                    if (HelperUtil.showLogs()) {
                        console.log(`The state of the session changed to ${state}`);
                    }

                    this.setState({
                        sessionState: state,
                        remoteVideoTrack: false,
                        localVideoTrack: false
                    }, () => {
                        switch (state) {
                            case SessionState.Initial:
                                break;
                            case SessionState.Establishing:
                                break;
                            case SessionState.Established:
                                this.stopAudioOutgoing();
                                this.setupRemoteMedia(session as Session);
                                break;
                            case SessionState.Terminating:
                            // fall through
                            case SessionState.Terminated:
                                if (HelperUtil.showLogs()) {
                                    console.log('The session was terminated', session);
                                }
                                this.stopAudioOutgoing();

                                this.cleanupRemoteMedia();

                                /*if (this.timeout) {
                                    clearTimeout(this.timeout);
                                }

                                this.timeout = setTimeout(() => {
                                    this.clearSession();
                                    this.cleanupRemoteMedia();
                                }, 2000);*/

                                break;
                            default:
                                throw new Error("Unknown session state.");

                        }
                    });
                });

                const requestDelegate = {
                    onReject: (response: IncomingResponse) => {
                        if (HelperUtil.showLogs()) {
                            console.log('Call rejected with response', response);
                        }
                        if (response && response.message && response.message.reasonPhrase) {
                            if (response.message.reasonPhrase === "Decline") {
                                this.setState({ rejectReason: i18next.t("declined") as any });
                            } else if (response.message.reasonPhrase === "Busy Here") {
                                this.setState({ rejectReason: i18next.t("busy") as any });
                            } else {
                                this.setState({ rejectReason: i18next.t("unavailable") as any });
                            }
                        }
                    }
                }

                this.setState({
                    session: session,
                    sessionState: SessionState.Initial,
                    rejectReason: '',
                    terminateReason: ''
                }, () => {
                    // Send the INVITE request
                    inviter.invite({
                        requestDelegate: requestDelegate
                    })
                        .then((request: any) => {
                            if (HelperUtil.showLogs()) {
                                console.log('Request for the invite', request);
                            }
                            // INVITE sent
                            this.setState({
                                sessionState: session.state,
                            }, () => {
                                this.playAudioOutgoing();
                            });
                        })
                        .catch((error: Error) => {
                            // INVITE did not send
                            if (error && (error.message === 'Permission denied' ||
                                error.message === 'The request is not allowed by the user agent or the platform in the current context.' ||
                                error.message === 'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.') &&
                                error.name === 'NotAllowedError') {
                                this.setState({
                                    catched: {
                                        error: new Error(i18next.t("no_device_permissions")),
                                        retry: true,
                                        retryTitle: i18next.t("recall"),
                                        retryFunc: () => {
                                            this.setState({
                                                catched: null
                                            }, () => {
                                                this.call(user, constrainsDefault);
                                            });
                                        }
                                    }
                                });
                            } else {
                                this.setState({
                                    catched: {
                                        error: new Error(i18next.t("call_error")),
                                        retry: true,
                                        retryTitle: i18next.t("recall"),
                                        retryFunc: () => {
                                            this.setState({
                                                catched: null
                                            }, () => {
                                                this.call(user, constrainsDefault);
                                            });
                                        }
                                    }
                                });
                            }

                            this.setState({
                                sessionState: session.state,
                            });
                        });
                });
            } else {
                if (HelperUtil.showLogs()) {
                    console.log('No user agent was found');
                }
            }
        });
    }

    endCall = (): void => {
        if (this.state.session) {
            if (HelperUtil.showLogs()) {
                console.log('Call was requested to end')
            }
            switch (this.state.session.state) {
                case SessionState.Initial:
                case SessionState.Establishing:
                    if (this.state.session instanceof Inviter) {
                        // An unestablished outgoing session
                        this.state.session.cancel();
                    } else {
                        // An unestablished incoming session
                        (this.state.session as Invitation).reject();
                    }
                    break;
                case SessionState.Established:
                    // An established session
                    this.state.session.bye();
                    break;
                case SessionState.Terminating:
                case SessionState.Terminated:
                    // Cannot terminate a session that is already terminated
                    break;
            }
        } else {
            if (HelperUtil.showLogs()) {
                console.log('No session to end found');
            }
        }
    }

    clearSession = (): void => {
        this.setState({
            session: null,
            sessionState: SessionState.Initial,
        });
    }

    setupRemoteMedia = (session: Session): void => {
        // Assumes you have a media element on the DOM
        const remoteStream = new MediaStream();

        (session?.sessionDescriptionHandler as any).peerConnection?.getReceivers().forEach((receiver: { track: MediaStreamTrack; }) => {
            if (receiver.track) {
                if (receiver.track.kind === 'video') {
                    this.setState({
                        remoteVideoTrack: true
                    });
                }
                remoteStream.addTrack(receiver.track);
            }
        });

        (session?.sessionDescriptionHandler as any).peerConnection?.getSenders().forEach((sender: { track: { kind: string; }; }) => {
            if (sender.track) {
                if (sender.track.kind === 'video') {
                    this.setState({
                        localVideoTrack: true
                    });
                }
            }
        });

        this.setupSpeakerVideo(this.state.selectedSpeaker, 'remote-video', () => {
            this.setupStreamToVideo(remoteStream, 'remote-video', () => {
            }, () => {
            });
        }, () => {
        });
    }

    cleanupRemoteMedia = (): void => {
        // Assumes you have a media element on the DOM
        this.setupStreamToVideo(null, 'remote-video', () => {
        }, () => {
        });
    }

    attemptReconnection = (reconnectionAttempt = 1): void => {
        // If not intentionally connected, don't reconnect.
        if (!this.shouldBeConnected) {
            return;
        }

        // Reconnection attempt already in progress
        if (this.attemptingReconnection) {
            return;
        }

        // Reconnection maximum attempts reached
        if (reconnectionAttempt > this.reconnectionAttempts) {
            return;
        }

        // We're attempting a reconnection
        this.attemptingReconnection = true;

        toast.error(i18next.t('connection_error'), {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
        });

        setTimeout(() => {
            // If not intentionally connected, don't reconnect.
            if (!this.shouldBeConnected) {
                this.attemptingReconnection = false;
                return;
            }
            // Attempt reconnect
            this.state.userAgent?.reconnect()
                .then(() => {
                    // Reconnect attempt succeeded
                    this.attemptingReconnection = false;
                    this.setState({ userAgentState: this.state.userAgent?.state as UserAgentState }, () => {

                    });
                })
                .catch((error: Error) => {
                    // Reconnect attempt failed
                    this.attemptingReconnection = false;
                    this.setState({ userAgentState: this.state.userAgent?.state as UserAgentState }, () => {

                    });
                    this.attemptReconnection(++reconnectionAttempt);
                });
        }, reconnectionAttempt === 1 ? 0 : this.reconnectionDelay * 1000);
    }

    generateFullUser = (user: string): string => {
        if (this.state.configurations) {
            if (!user.includes(`@${this.state.configurations.domain}`)) {
                user = `${user}@${this.state.configurations.domain}`;
            }
        }

        return user;
    }

    // ----------------------------------------------------------------------

    toTimeString(seconds: number): string {
        // @ts-ignore
        return (new Date(seconds * 1000)).toUTCString().match(/(\d\d:\d\d:\d\d)/)[0];
    }

}

export default App;
